import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import TitleRow from '@/components/ui/TitleRow'
import AdvancedScreener, {
  AdvancedScreenerSkeleton,
} from '@/features/screener/components/AdvancedScreener'
import { parseScreenState, screenStateSchema } from '@/features/screener/lib/screenState'
import { loadAdvancedScreener } from '@/screens/screener/AdvancedScreenerScreen.loader'

const TITLE = 'Advanced'

/** The section's name over the page's, as the mock heads it ("Screener", then the page). */
const HEADER = <TitleRow eyebrow="Screener" title={TITLE} />

function AdvancedScreenerPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading the screener…" header={HEADER} pageTitle={TITLE}>
      <AdvancedScreenerSkeleton />
    </ScreenLayout>
  )
}

function AdvancedScreenerError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn’t load the screener"
      description="The data did not come back. Nothing on your device is wrong."
    />
  )
}

export const Route = createFileRoute('/_app/screener/advanced')({
  validateSearch: screenStateSchema,
  loader: loadAdvancedScreener,
  pendingComponent: AdvancedScreenerPending,
  errorComponent: AdvancedScreenerError,
  component: AdvancedScreenerScreen,
})

/**
 * Screener · Advanced: the screener with its state in the URL, so a link reopens the same screen
 * (the filters, sector, order, page, page size, column set and view). The page is the shared
 * right rail and the Ask bar's "Screener · Advanced" around `AdvancedScreener`, which the chart
 * workspace also mounts as a tab.
 */
function AdvancedScreenerScreen() {
  // Re-parsed so a stray param (`?utm_source=`) never keys the page apart from the loader's.
  const state = parseScreenState(Route.useSearch())
  const navigate = Route.useNavigate()

  return (
    <ScreenLayout header={HEADER} pageTitle={TITLE}>
      <AdvancedScreener
        state={state}
        onStateChange={(next) => {
          void navigate({ search: next })
        }}
      />
    </ScreenLayout>
  )
}
